import { useState } from "react";
import { inviteTeamMember, updateUserCrewLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { KeyRound, Unlink, Loader2 } from "lucide-react";

/**
 * Owner-only crew-card section for managing a crew member's portal login.
 * A crew-role login only sees events assigned to the crew profile it is
 * linked to — this widget creates/links/unlinks that connection in place.
 */
export function PortalAccess({ crewMember, team, onRefresh }) {
  const linked = team.find(u => u.crew_id === crewMember.crew_id);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const openPicker = () => {
    // Prefill with the crew member's first email on file.
    setEmail(crewMember.emails?.[0] || crewMember.email || "");
    setOpen(true);
  };

  const grant = async () => {
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const existing = team.find(u => u.email?.toLowerCase() === em);
      if (existing) {
        await updateUserCrewLink(existing.user_id, crewMember.crew_id);
        toast.success(`Linked ${em} to ${crewMember.name}`);
      } else {
        await inviteTeamMember({ email: em, name: crewMember.name, role: "crew", crew_id: crewMember.crew_id });
        toast.success(`${em} can now sign in with Google`);
      }
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant access");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await updateUserCrewLink(linked.user_id, null);
      toast.success("Portal access unlinked");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unlink");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`portal-access-${crewMember.crew_id}`} className="mt-2 pt-2 border-t border-border">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
          <KeyRound className="w-2.5 h-2.5" /> Portal
        </span>
        {linked ? (
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-[10px] text-emerald-400 truncate" title={linked.email}>{linked.email}</span>
            <Button
              data-testid={`portal-unlink-${crewMember.crew_id}`}
              variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-red-400 shrink-0"
              onClick={unlink} disabled={busy} title="Remove portal access from this crew member"
            >
              {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Unlink className="w-2.5 h-2.5" />}
            </Button>
          </div>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid={`portal-grant-${crewMember.crew_id}`}
                variant="ghost" size="sm" className="h-5 text-[10px] text-primary px-1.5"
                onClick={openPicker}
              >
                Grant access
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 max-w-[calc(100vw-2rem)] p-3 space-y-2" align="end">
              <p className="text-xs text-muted-foreground">
                They'll sign in with Google using this email and see only {crewMember.name}'s assigned events.
              </p>
              <Input
                data-testid={`portal-email-${crewMember.crew_id}`}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="their-email@example.com"
                className="h-8 text-sm bg-secondary/30 border-border"
              />
              <Button
                data-testid={`portal-confirm-${crewMember.crew_id}`}
                onClick={grant} disabled={busy}
                className="w-full h-8 bg-primary text-primary-foreground hover:bg-primary/90 text-xs uppercase tracking-wider"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Grant portal access"}
              </Button>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
